import { Color } from './color';

/**
 * The functions in this file change the current colour scheme that is used throughout the app, by updating the
 * values of the relevant CSS variables, to contain the new specified values.
 *
 * Portions of this functionality are based on some of the code used within the 'Ionic New Color Creator'
 * (see https://ionicframework.com/docs/theming/colors#new-color-creator) to generate the values of some
 * Ionic CSS variables, based upon a specified colour hex code.
 *
 * ----------
 * - Usage: -
 * ----------
 *
 * 1. Import this function at the top of any React component file that needs to update the app colour scheme:
 *
 * import { updateAppColours } from '../helpers/updateAppColours';
 *
 * 2. Add code similar to this within the component (this code will update the app colours based on the current
 * colours stored in the app state, which can be retrieved from the AppContext):
 *
 * updateAppColours(state.config.data.colours);
 *
 * @category   GenerateUK
 * @package    wellonline-pwa
 * @author     Patrick Hathway - Generate UK
 *
 * @param {Object} colours
 *      Object containing the 'keys' and associated new hex values of the colours in the app that need to be changed,
 *      e.g. {primary: '#7FBC03', secondary: '#707070', ...}
 */
export function updateAppColours(colours) {
    // Update each of the specified colours (except for the default background colour, which is handled separately)
    for(let colourKey in colours) {
        if( ! colours.hasOwnProperty(colourKey) || colourKey === 'bg_default') {
            continue;
        }
        
        // Update the CSS variables corresponding to the current colour
        updateAppColour(colourKey, colours[colourKey]);
    }
    
    // Update the global Ionic CSS variable for the default background colour (if specified), to contain the new value
    if(colours.hasOwnProperty('bg_default')) {
        document.documentElement.style.setProperty('--ion-background-color', colours.bg_default);
    }
    
    // Update browser address bar colour in mobile Chrome browsers
    if(colours.hasOwnProperty('primary')) {
        document.querySelector('meta[name="theme-color"]').setAttribute(
            'content', colours.primary);
    }
}

/**
 * Update the global values of all of the CSS variables associated with the specified colour 'key', to contain the
 * corresponding hex code of the new colour (and/or the hex code(s) or RGB values of similar variant colours, which
 * are generated automatically based on the specified original colour hex code).
 *
 * Some of the code in this function is loosely based on the code in the `generateColor()` function in
 * https://github.com/ionic-team/ionic-docs/blob/master/src/components/color-gen/parse-css.ts, which is used by the
 * 'Ionic New Color Creator' (see https://ionicframework.com/docs/theming/colors#new-color-creator) to auto-generate
 * the colour values that will be output within some Ionic CSS variables, based upon a specified colour hex code.
 *
 * @param {string} colourKey
 *      The colour 'key' that used to identify which CSS variables to update. For example: 'primary'
 * @param {string} colourHexCode
 *      The value of all applicable colour CSS variables will be updated globally based on the specified hex colour
 *      code. For example: '#7FBC03'
 */
const updateAppColour = (colourKey, colourHexCode) => {
    // Determine the base name of all applicable CSS variables, based on the specified colour key
    let baseColourCssVarName = '--ion-color-' + colourKey.replace('_', '-');
    
    // Auto-generate variants of the specified colour, based on the supplied colour hex code
    const colourVariants = new Color(colourHexCode);
    const contrast = colourVariants.contrast();
    const tint = colourVariants.tint();
    const shade = colourVariants.shade();
    
    /* Globally update the values of all applicable CSS variables, based on the supplied colour hex code
       and the auto-generated colour variants */
    document.documentElement.style.setProperty(
        baseColourCssVarName, colourHexCode);
    document.documentElement.style.setProperty(
        baseColourCssVarName + '-rgb', rgbToString(colourVariants.rgb));
    document.documentElement.style.setProperty(
        baseColourCssVarName + '-contrast', contrast.hex);
    document.documentElement.style.setProperty(
        baseColourCssVarName + '-contrast-rgb', rgbToString(contrast.rgb));
    document.documentElement.style.setProperty(
        baseColourCssVarName + '-shade', shade.hex);
    document.documentElement.style.setProperty(
        baseColourCssVarName + '-tint', tint.hex);
};

/**
 * Format the RGB values of the specified colour object, so that they can be used within a CSS variable.
 *
 * @param {{r: number, g: number, b: number}} c
 *      Object containing red, green and blue colour values
 *
 * @returns {string}
 *      Comma separated string containing the relevant red, green and blue colour values
 */
const rgbToString = (c) => {
    return `${c.r},${c.g},${c.b}`;
};